import { useRef, useState } from "react";
import axios from "axios";
import { Loader2, Upload, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { API, apiError, authHeaders, mediaUrl, DEFAULT_CONFIG, SITE } from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function pollJob(jobId) {
  for (;;) {
    await sleep(3000);
    const { data } = await axios.get(`${API}/admin/upload/video/${jobId}`, { headers: authHeaders() });
    if (data.status !== "processing") return data;
  }
}

export const VideoUploader = ({ value, onChange }) => {
  const input = useRef(null);
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);

  const upload = async (file) => {
    if (!file) return;
    setPhase("uploading");
    setProgress(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload/video`, form, {
        headers: authHeaders(),
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      setPhase("processing");
      const job = await pollJob(data.jobId);
      if (job.status === "error") throw new Error(job.detail);
      onChange(job.url);
      toast.success("Video aggiornato sul sito");
    } catch (err) {
      toast.error(err.response ? apiError(err) : err.message);
    } finally {
      setPhase("idle");
      if (input.current) input.current.value = "";
    }
  };

  const busy = phase !== "idle";
  return (
    <div className="flex flex-col gap-3" data-testid="admin-video-uploader">
      <video key={value} src={mediaUrl(value)} poster={value === DEFAULT_CONFIG.heroVideo ? SITE.poster : undefined} className="w-full max-w-[220px] aspect-[9/16] object-cover rounded-2xl bg-white/5" muted autoPlay loop playsInline />
      <input ref={input} type="file" accept="video/mp4,video/quicktime,video/webm,.mov,.mp4" className="hidden" data-testid="admin-video-input" onChange={(e) => upload(e.target.files[0])} />
      {phase === "uploading" && <div className="flex flex-col gap-1.5"><Progress value={progress} className="h-1.5 bg-white/10" /><span className="text-xs text-white/50">Caricamento {progress}%</span></div>}
      {phase === "processing" && <span className="text-xs text-white/60 flex items-center gap-2" data-testid="admin-video-processing"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Ottimizzo il video per il sito, può volerci un minuto…</span>}
      <div className="flex gap-2 flex-wrap">
        <button type="button" disabled={busy} onClick={() => input.current?.click()} data-testid="admin-video-upload-button"
          className="bg-white text-black rounded-full px-5 py-2.5 text-sm font-medium flex items-center gap-2 hover:opacity-90 disabled:opacity-50">
          <Upload className="w-4 h-4" /> Carica nuovo video
        </button>
        {value !== DEFAULT_CONFIG.heroVideo && (
          <button type="button" disabled={busy} onClick={() => onChange(DEFAULT_CONFIG.heroVideo)} data-testid="admin-video-reset-button"
            className="glass rounded-full px-4 py-2.5 text-sm text-white/80 hover:text-white flex items-center gap-2 disabled:opacity-50">
            <Undo2 className="w-4 h-4" /> Video originale
          </button>
        )}
      </div>
    </div>
  );
};
